import { useCallback, useMemo } from "react"
import { MusdLiquityProps } from "@mezo/sdk"
import {
  abi as borrowOperationsAbi,
  address as borrowOperationsAddress,
} from "@mezo-org/musd-contracts/deployments/mainnet/BorrowerOperations.json"
import {
  abi as interestRateManagerAbi,
  address as interestRateManagerAddress,
} from "@mezo-org/musd-contracts/deployments/mainnet/InterestRateManager.json"
import {
  abi as troveManagerAbi,
  address as troveManagerAddress,
} from "@mezo-org/musd-contracts/deployments/mainnet/TroveManager.json"
import { address as borrowOperationsTestnetAddress } from "@mezo-org/musd-contracts/deployments/matsnet/BorrowerOperations.json"
import { address as interestRateManagerTestnetAddress } from "@mezo-org/musd-contracts/deployments/matsnet/InterestRateManager.json"
import { address as troveManagerTestnetAddress } from "@mezo-org/musd-contracts/deployments/matsnet/TroveManager.json"
import { Address } from "viem"
import { useReadContracts } from "wagmi"

import {
  selectCollateralPrice,
  setBorrowLiquity,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { IS_MEZO_MAINNET, MEZO_CHAIN } from "#/shared/constants"

const useMusdLiquityProps = () => {
  const collateralPrice = useDappSelector(selectCollateralPrice)
  const dispatch = useDappDispatch()

  const borrowerOperationContract = useMemo(
    () => ({
      chainId: MEZO_CHAIN.id,
      abi: borrowOperationsAbi,
      address: (IS_MEZO_MAINNET
        ? borrowOperationsAddress
        : borrowOperationsTestnetAddress) as Address,
    }),
    [],
  )

  const interestRateManagerContract = useMemo(
    () => ({
      chainId: MEZO_CHAIN.id,
      abi: interestRateManagerAbi,
      address: (IS_MEZO_MAINNET
        ? interestRateManagerAddress
        : interestRateManagerTestnetAddress) as Address,
    }),
    [],
  )

  const troveManagerContract = useMemo(
    () => ({
      chainId: MEZO_CHAIN.id,
      abi: troveManagerAbi,
      address: (IS_MEZO_MAINNET
        ? troveManagerAddress
        : troveManagerTestnetAddress) as Address,
    }),
    [],
  )

  const { refetch: fetchLiquityContractData } = useReadContracts({
    contracts: [
      {
        ...troveManagerContract,
        functionName: "checkRecoveryMode",
        args: [collateralPrice ?? 0n],
      },
      { ...borrowerOperationContract, functionName: "CCR" },
      { ...borrowerOperationContract, functionName: "MCR" },
      { ...borrowerOperationContract, functionName: "MUSD_GAS_COMPENSATION" },
      { ...borrowerOperationContract, functionName: "borrowingRate" },
      { ...borrowerOperationContract, functionName: "minNetDebt" },
      { ...interestRateManagerContract, functionName: "interestRate" },
    ],
    multicallAddress: "0xca11bde05977b3631167028862be2a173976ca11",
  })

  const fetchMusdLiquityProps = useCallback(async () => {
    const { data } = await fetchLiquityContractData()

    if (!data) return

    const values = data.map((item) => item.result)

    const liquityProps: MusdLiquityProps = {
      isRecoveryModeEnabled: (values[0] as boolean) ?? false,
      criticalCollateralRatio: (values[1] as bigint) ?? 0n,
      minimumCollateralRatio: (values[2] as bigint) ?? 0n,
      gasCompensation: (values[3] as bigint) ?? 0n,
      borrowingFeeRate: (values[4] as bigint) ?? 0n,
      minimumNetDebt: (values[5] as bigint) ?? 0n,
      interestRate: BigInt((values[6] as number) ?? 0),
    }

    dispatch(setBorrowLiquity(liquityProps))
  }, [fetchLiquityContractData, dispatch])

  return { fetchMusdLiquityProps }
}

export default useMusdLiquityProps
